import React, { useState, useEffect, useMemo } from 'react';
import {
  Car,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  MoreHorizontal,
  Package,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Truck,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import AppBar from 'components/AppBar';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import { getPurchaseOrderByPONumber, getAllPurchaseOrders, syncPurchaseOrderInvoicing, parsePoCurrency, getPoGrandTotal, getInvoiceBookedTotal } from '../services/purchaseOrdersService';
import { getAllInvoices, createInvoice, updateInvoice, checkInvoiceExists } from '../services/invoiceTrackingService';
import './InvoiceTracking.css';
import { useLotScope } from '../hooks/useLotScope';

/** Workflow order: Admin review → Pending Signature → Pending Payment → Pending → Paid. */
const INVOICE_TRACKING_STATUS_OPTIONS = [
  'Admin review',
  'Pending Signature',
  'Pending Payment',
  'Pending',
  'Paid'
];

/** Table sort rank — lower appears higher in the list. */
function invoiceStatusSortRank(status) {
  const badge = invoiceStatusBadgeClass(status);
  if (badge === 'admin-review') return 0;
  if (badge === 'pending-signature') return 1;
  if (badge === 'pending-payment') return 2;
  if (badge === 'pending') return 3;
  if (badge === 'paid') return 4;
  return 5;
}

function isInvoiceLogisticsPaidStatus(status) {
  return String(status || '').trim().toLowerCase() === 'paid';
}

function isPendingSignatureStatus(status) {
  return invoiceStatusBadgeClass(status) === 'pending-signature';
}

function invoiceStatusFilterKey(status) {
  return String(status || '').trim().toLowerCase();
}

function invoiceStatusBadgeClass(status) {
  const k = invoiceStatusFilterKey(status);
  if (!k) return 'unknown';
  return k.replace(/\s+/g, '-');
}

const PO_LOT_TAB_OPTIONS = [
  { value: 'all', label: 'All LOTs', variant: 'all' },
  { value: 'LOT 8 (Morogoro)', label: 'LOT 8', variant: '8' },
  { value: 'LOT 10 (Manyara)', label: 'LOT 10', variant: '10' }
];

function invoiceRowKey(inv) {
  return String(inv?.documentId || inv?.id || '');
}

/** Table display: first 6 characters of LOT (full value in title tooltip). */
function formatLotNameTableCell(lot) {
  if (lot == null || String(lot).trim() === '') return '-';
  const s = String(lot).trim();
  return s.length > 6 ? `${s.slice(0, 6)}…` : s;
}

/** Table display: first word of supplier only (full value in title tooltip). */
function formatSupplierNameTableCell(vendor) {
  if (vendor == null || String(vendor).trim() === '') return '-';
  const s = String(vendor).trim();
  const first = s.split(/\s+/)[0];
  return first || '-';
}

/**
 * Trim the description to the first N words so the column stays narrow while still
 * conveying meaning. The full value is restored via the cell's title tooltip on hover.
 */
function truncateDescriptionWords(value, maxWords = 3) {
  const text = String(value || '').trim();
  if (!text) return '-';
  const words = text.split(/\s+/);
  if (words.length <= maxWords) return text;
  return `${words.slice(0, maxWords).join(' ')}…`;
}

/** Remaining PO balance = (PO + VAT) − all booked invoice totals. */
function getPoOpenForInvoicing(po) {
  if (!po) return 0;
  const balance = po._raw?.poBalance ?? parsePoCurrency(po.poBalance);
  if (Number.isFinite(balance)) return Math.max(0, balance);
  return Math.max(0, getPoGrandTotal(po) - parsePoCurrency(po.invoicedAmount));
}


function invoicePoGroupKey(invoice) {
  return `${String(invoice?.poId || '').trim()}||${String(invoice?.lot || '').trim()}`;
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Format a Date as 23/Mar/2026 (Invoice Tracking standard). */
function formatInvoiceTrackingDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  return `${day}/${MONTH_ABBR[date.getMonth()]}/${date.getFullYear()}`;
}

/** Parse DD/Mon/YYYY or legacy DD/MM/YYYY into a Date (local). */
function parseInvoiceTrackingDate(dateString) {
  if (!dateString) return null;
  const parts = String(dateString).trim().split('/');
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const year = parseInt(parts[2], 10);
  if (!Number.isFinite(day) || !Number.isFinite(year)) return null;

  let monthIndex;
  const monthPart = String(parts[1] || '').trim();
  if (/^\d{1,2}$/.test(monthPart)) {
    monthIndex = parseInt(monthPart, 10) - 1;
  } else {
    monthIndex = MONTH_ABBR.findIndex((m) => m.toLowerCase() === monthPart.toLowerCase());
  }
  if (monthIndex < 0 || monthIndex > 11) return null;

  const date = new Date(year, monthIndex, day);
  if (date.getFullYear() !== year || date.getMonth() !== monthIndex || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** Convert stored display date → YYYY-MM-DD for <input type="date">. */
function toHtmlDateInputValue(dateString) {
  const date = parseInvoiceTrackingDate(dateString);
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Convert YYYY-MM-DD from date picker → 23/Mar/2026. */
function fromHtmlDateInputValue(isoDate) {
  if (!isoDate) return '';
  const [y, m, d] = String(isoDate).split('-').map((p) => parseInt(p, 10));
  if (!y || !m || !d) return '';
  return formatInvoiceTrackingDate(new Date(y, m - 1, d));
}

/** Show stored dates in DD/Mon/YYYY (normalizes legacy DD/MM/YYYY). */
function displayInvoiceTrackingDate(dateString) {
  if (!dateString) return '';
  const parsed = parseInvoiceTrackingDate(dateString);
  return parsed ? formatInvoiceTrackingDate(parsed) : String(dateString).trim();
}

function findPoForInvoice(invoice, purchaseOrders) {
  const poNum = String(invoice?.poId || '').trim();
  if (!poNum || !purchaseOrders?.length) return null;
  const lot = String(invoice?.lot || '').trim();
  const matches = purchaseOrders.filter((po) => String(po.poNumber || '').trim() === poNum);
  if (matches.length === 0) return null;
  if (lot) {
    const lotMatch = matches.find((po) => String(po.lot || '').trim() === lot);
    if (lotMatch) return lotMatch;
  }
  return matches[0];
}

const poHasOpenBalance = (po) => getPoOpenForInvoicing(po) > 0;

function InvPageSkeleton() {
  return (
    <div className="inv-skeleton" aria-busy="true" aria-label="Loading invoices">
      <section className="inv-intro" aria-hidden>
        <div className="inv-intro-text">
          <Skeleton className="h-7 w-48" />
          <Skeleton className="mt-2.5 h-4 w-80 max-w-full" />
        </div>
        <div className="inv-intro-actions">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <Skeleton className="h-9 w-[8.5rem] rounded-lg" />
        </div>
      </section>
      <div className="inv-lot-tabs" aria-hidden>
        <Skeleton className="h-9 w-24 rounded-md" />
        <Skeleton className="h-9 w-20 rounded-md" />
        <Skeleton className="h-9 w-20 rounded-md" />
      </div>
      <div className="inv-categories-grid" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="inv-category-card inv-category-card--skel">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <Skeleton className="mt-3 h-4 w-24" />
            <Skeleton className="mt-2 h-6 w-36" />
            <Skeleton className="mt-4 h-3 w-full" />
            <Skeleton className="mt-2 h-3 w-[80%]" />
          </div>
        ))}
      </div>
      <div className="inv-toolbar" aria-hidden>
        <Skeleton className="h-9 w-full max-w-md rounded-lg" />
        <div className="inv-toolbar__spacer" />
        <Skeleton className="h-9 w-28 rounded-lg" />
        <Skeleton className="h-9 w-28 rounded-lg" />
      </div>
      <div className="inv-table-section" aria-hidden>
        <div className="inv-table-shell">
          <div className="inv-skel-table-rows">
            {Array.from({ length: 6 }, (_, r) => (
              <Skeleton key={r} className="h-10 w-full rounded-md" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InvoiceTracking() {
  const [selectedCategory, setSelectedCategory] = useState('material');
  const [searchQuery, setSearchQuery] = useState('');
  const { lotFilter, setLotFilter, lockedLotFilter, isReadOnly } = useLotScope('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editStatus, setEditStatus] = useState('');
  const [editPaidDate, setEditPaidDate] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  /** Inline PR No edit (same pattern as Rental Invoice Tracking on Payment Trucking). */
  const [editingPrRowKey, setEditingPrRowKey] = useState('');
  const [editPrNoInlineValue, setEditPrNoInlineValue] = useState('');
  /** Inline Pending level edit — only when status is Pending Signature. */
  const [editingPendingLevelRowKey, setEditingPendingLevelRowKey] = useState('');
  const [editPendingLevelInlineValue, setEditPendingLevelInlineValue] = useState('');
  const [showNewInvoiceModal, setShowNewInvoiceModal] = useState(false);
  const [newInvoiceForm, setNewInvoiceForm] = useState({
    invoiceNo: '',
    poNumber: '',
    prNo: '',
    invoiceDate: '',
    dueDate: '',
    amountInvoiced: ''
  });
  const [includeVat, setIncludeVat] = useState(false);
  const [poData, setPoData] = useState(null);
  const [loadingPO, setLoadingPO] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [availablePOs, setAvailablePOs] = useState([]);
  const [loadingPOs, setLoadingPOs] = useState(false);
  /** Optional LOT filter for the New Invoice dialog ("" = show every LOT). */
  const [newInvoiceLotFilter, setNewInvoiceLotFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const [purchaseOrdersForStats, setPurchaseOrdersForStats] = useState([]);

  // Helper function to parse currency string to number
  const parseCurrency = (currencyString) => {
    if (!currencyString) return 0;
    // Remove "TZS " and commas, then parse to number
    const cleaned = currencyString.replace(/TZS\s?/gi, '').replace(/,/g, '').trim();
    return parseFloat(cleaned) || 0;
  };

  // Helper function to format number as currency
  const formatCurrency = (amount) => {
    return `TZS ${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Helper function to determine category from subcategory
  const getCategoryFromSubcategory = (subcategory) => {
    if (!subcategory) return 'others';
    const sub = subcategory.toLowerCase().trim();
    
    // Transportation subcategories (check first to avoid false matches
    // e.g. "Local transportation of Wooden poles" must stay transportation, not material).
    // "shipping" is the PO subcategory used for transportation rows.
    if (
      sub.includes('transportation') ||
      sub.includes('delivery') ||
      sub.includes('shipping')
    ) {
      return 'transportation';
    }
    
    // Fleet subcategories (SUV, Pick up, Crane, Fuel — legacy carne/crene spellings still map to fleet)
    if (
      sub === 'suv' ||
      sub === 'fuel' ||
      sub === 'pick up' ||
      sub === 'pickup' ||
      sub === 'pick-up' ||
      sub === 'carne' ||
      sub === 'crene' ||
      sub === 'crane'
    ) {
      return 'fleet';
    }
    
    // Material: exact or contained keyword so variants like "concrete poles" / "wooden poles" map correctly
    const materialKeywords = ['wooden poles', 'poles', 'cables', 'transformers', 'meters', 'fittings'];
    if (materialKeywords.some((keyword) => sub === keyword || sub.includes(keyword))) {
      return 'material';
    }
    
    // Default to others
    return 'others';
  };

  // Due date is entered manually (format: 23/Mar/2026) — no auto +15 days.

  const [invoices, setInvoices] = useState([]);

  // Load invoices from database
  const reloadInvoices = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllInvoices();
      setInvoices(data);
      if (data.length === 0) {
        console.log('No invoices found. Collection may need to be set up. Run: npm run setup:invoice-tracking');
      }
    } catch (err) {
      console.error('Error loading invoices:', err);
      const msg = err.message || '';
      if (err.code === 'CORS_ERROR' || msg.includes('Appwrite') || msg.includes('CORS') || msg.includes('Failed to fetch')) {
        setError('Data cannot load: Add domain in Appwrite. Go to cloud.appwrite.io → Your Project → Settings → Platforms → Add Platform → Web → Hostname: sagemcomlogistics.com (no https://). Then click Retry below.');
      } else if (err.code === 404 || msg.includes('not found') || msg.includes('Collection')) {
        setError('Invoice tracking collection not found. Run: npm run setup:invoice-tracking');
      } else {
        setError(msg || 'Failed to load invoices. Add sagemcomlogistics.com in Appwrite Platforms (hostname only, no https://) then Retry.');
      }
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadInvoices();
  }, []);

  useEffect(() => {
    let cancelled = false;
    getAllPurchaseOrders()
      .then((list) => {
        if (!cancelled) setPurchaseOrdersForStats(list || []);
      })
      .catch((err) => {
        console.error('Error loading POs for invoice stats:', err);
        if (!cancelled) setPurchaseOrdersForStats([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sample invoices removed - now loading from database

  // Calculate category statistics dynamically from invoices + PO invoiced totals
  const categories = useMemo(() => {
    const categoryStats = {
      material: { invoiceCount: 0, totalAmount: 0, amountInvoiced: 0, pendingAmount: 0, poInvoicedAmount: 0, poTotalAmount: 0 },
      transportation: { invoiceCount: 0, totalAmount: 0, amountInvoiced: 0, pendingAmount: 0, poInvoicedAmount: 0, poTotalAmount: 0 },
      fleet: { invoiceCount: 0, totalAmount: 0, amountInvoiced: 0, pendingAmount: 0, poInvoicedAmount: 0, poTotalAmount: 0 },
      others: { invoiceCount: 0, totalAmount: 0, amountInvoiced: 0, pendingAmount: 0, poInvoicedAmount: 0, poTotalAmount: 0 }
    };

    // Calculate stats for each invoice
    invoices.forEach(invoice => {
      const category = getCategoryFromSubcategory(invoice.subcategory);
      const stats = categoryStats[category];
      
      if (stats) {
        stats.invoiceCount += 1;
        const total = parseCurrency(invoice.totalAmount);
        const amountInvoiced = parseCurrency(invoice.amount);
        stats.totalAmount += total;
        stats.amountInvoiced += amountInvoiced;
        
        if (!isInvoiceLogisticsPaidStatus(invoice.status)) {
          stats.pendingAmount += total;
        }
      }
    });

    (purchaseOrdersForStats || []).forEach((po) => {
      const cat = String(po.category || 'others').toLowerCase().trim();
      const key = categoryStats[cat] ? cat : 'others';
      categoryStats[key].poInvoicedAmount += parsePoCurrency(po.invoicedAmount);
      categoryStats[key].poTotalAmount += parsePoCurrency(po.totalAmount);
    });

    const buildCategory = (id, label, icon, color, stats) => ({
      id,
      label,
      icon,
      color,
      invoiceCount: stats.invoiceCount,
      totalAmount: formatCurrency(stats.totalAmount),
      amountInvoiced: formatCurrency(stats.amountInvoiced),
      poInvoicedPercent:
        stats.poTotalAmount > 0
          ? `${((stats.poInvoicedAmount / stats.poTotalAmount) * 100).toFixed(1)}%`
          : '0.0%',
      poInvoicedAmount: formatCurrency(stats.poInvoicedAmount),
      pendingPercent:
        stats.totalAmount > 0
          ? `${((stats.pendingAmount / stats.totalAmount) * 100).toFixed(1)}%`
          : '0.0%',
      pendingAmount: formatCurrency(stats.pendingAmount),
      isPaid: stats.amountInvoiced >= stats.totalAmount && stats.totalAmount > 0
    });

    // Build categories array with calculated data
    return [
      buildCategory('material', 'Material', Package, '#1e90ff', categoryStats.material),
      buildCategory('transportation', 'Transportation', Truck, '#10b981', categoryStats.transportation),
      buildCategory('fleet', 'Fleet', Car, '#f59e0b', categoryStats.fleet),
      buildCategory('others', 'Others', MoreHorizontal, '#64748b', categoryStats.others)
    ];
  }, [invoices, purchaseOrdersForStats]);

  /** Invoices whose amount exceeds PO total or belong to an over-invoiced PO. */
  const invoiceAmountWarnings = useMemo(() => {
    const warnings = new Map();
    const invoicedByPo = new Map();

    invoices.forEach((inv) => {
      const key = invoicePoGroupKey(inv);
      invoicedByPo.set(key, (invoicedByPo.get(key) || 0) + getInvoiceBookedTotal(inv));
    });

    invoices.forEach((inv) => {
      const rowKey = invoiceRowKey(inv);
      if (!rowKey) return;

      const po = findPoForInvoice(inv, purchaseOrdersForStats);
      if (!po) return;

      const poTotal = getPoGrandTotal(po);
      const invoiceAmt = getInvoiceBookedTotal(inv);
      const groupKey = invoicePoGroupKey(inv);
      const totalInvoiced = invoicedByPo.get(groupKey) || invoiceAmt;
      const poLabel = String(inv.poId || po.poNumber || '').trim();

      if (invoiceAmt > poTotal) {
        warnings.set(
          rowKey,
          `Invoice total (${formatCurrency(invoiceAmt)}) exceeds PO total incl. VAT (${formatCurrency(poTotal)}).`
        );
      } else if (totalInvoiced > poTotal) {
        warnings.set(
          rowKey,
          `PO ${poLabel} is over-invoiced: total booked ${formatCurrency(totalInvoiced)} exceeds PO total incl. VAT ${formatCurrency(poTotal)}.`
        );
      }
    });

    return warnings;
  }, [invoices, purchaseOrdersForStats]);

  /** Filter + edit dropdowns: workflow order plus any legacy status on stored invoices. */
  const allInvoiceStatusesForUi = useMemo(() => {
    const ordered = [...INVOICE_TRACKING_STATUS_OPTIONS];
    const seen = new Set(ordered.map((s) => invoiceStatusFilterKey(s)));
    invoices.forEach((inv) => {
      const raw = String(inv.status || '').trim();
      if (!raw) return;
      const k = invoiceStatusFilterKey(raw);
      if (!seen.has(k)) {
        seen.add(k);
        ordered.push(raw);
      }
    });
    return ordered.sort((a, b) => invoiceStatusSortRank(a) - invoiceStatusSortRank(b));
  }, [invoices]);

  // Load all POs from database when modal opens.
  // We deliberately keep EVERY document (no dedup by poNumber) so that POs that share a
  // number across different LOTs/suppliers all remain selectable — the LOT filter and
  // datalist labels disambiguate them. Normalise poNumber so trailing spaces, etc. don't
  // hide rows from the type-ahead.
  useEffect(() => {
    if (showNewInvoiceModal) {
      const loadPOs = async () => {
        setLoadingPOs(true);
        try {
          const allPOs = await getAllPurchaseOrders();
          const cleaned = (allPOs || [])
            .map((po) => ({
              ...po,
              poNumber: String(po.poNumber || '').trim()
            }))
            .filter((po) => po.poNumber !== '' && poHasOpenBalance(po));
          setAvailablePOs(cleaned);
        } catch (error) {
          console.error('Error loading POs:', error);
          setAvailablePOs([]);
        } finally {
          setLoadingPOs(false);
        }
      };
      loadPOs();
    }
  }, [showNewInvoiceModal]);

  // Fetch PO data when PO number is selected.
  // 1) Prefer the locally cached `availablePOs` (already paginated + un-deduped) so we can
  //    honour the LOT filter when several PO docs share a number.
  // 2) Fall back to a direct Appwrite lookup only if we have no local match (e.g. the user
  //    pasted a number before the list finished loading).
  const handlePONumberChange = async (poNumber) => {
    const trimmed = String(poNumber || '').trim();
    setNewInvoiceForm(prev => ({ ...prev, poNumber: trimmed }));
    setPoData(null);
    setInvoiceError(null);

    if (!trimmed) {
      return;
    }

    const localMatches = (availablePOs || []).filter(
      (po) => String(po.poNumber || '').trim() === trimmed
    );

    // Narrow by LOT if the user picked one — this is the key disambiguation step when the
    // same PO number is recorded under multiple LOTs.
    const lotNarrowed = newInvoiceLotFilter
      ? localMatches.filter((po) => String(po.lot || '').trim() === newInvoiceLotFilter)
      : localMatches;

    if (lotNarrowed.length > 0) {
      const withBalance = lotNarrowed.filter(poHasOpenBalance);
      if (withBalance.length === 0) {
        setPoData(null);
        setInvoiceError(
          'This PO is fully invoiced (PO balance is zero) and cannot be used for a new invoice.'
        );
        return;
      }
      setPoData(withBalance[0]);
      setInvoiceError(
        withBalance.length > 1
          ? `Found ${withBalance.length} POs with this number. Showing the first — narrow with the LOT filter if needed.`
          : null
      );
      return;
    }

    setLoadingPO(true);
    try {
      const po = await getPurchaseOrderByPONumber(trimmed);
      if (po) {
        if (!poHasOpenBalance(po)) {
          setPoData(null);
          setInvoiceError(
            'This PO is fully invoiced (PO balance is zero) and cannot be used for a new invoice.'
          );
        } else {
          setPoData(po);
          setInvoiceError(null);
        }
      } else {
        setPoData(null);
        setInvoiceError('Purchase Order not found. Please check the PO number.');
      }
    } catch (error) {
      console.error('Error fetching PO:', error);
      setPoData(null);
      setInvoiceError('Error fetching Purchase Order. Please try again.');
    } finally {
      setLoadingPO(false);
    }
  };

  // Handle new invoice form submission
  const handleNewInvoiceSubmit = async (e) => {
    e.preventDefault();
    if (isSavingInvoice) return;
    
    if (!newInvoiceForm.invoiceNo || !newInvoiceForm.poNumber || !newInvoiceForm.invoiceDate || !newInvoiceForm.dueDate || !newInvoiceForm.amountInvoiced) {
      alert('Please fill in all required fields');
      return;
    }

    if (!parseInvoiceTrackingDate(newInvoiceForm.invoiceDate)) {
      alert('Please enter a valid invoice date (e.g. 23/Mar/2026).');
      return;
    }

    if (!parseInvoiceTrackingDate(newInvoiceForm.dueDate)) {
      alert('Please enter a valid due date (e.g. 23/Mar/2026).');
      return;
    }

    if (!poData) {
      alert('Please enter a valid PO number');
      return;
    }

    if (!poHasOpenBalance(poData)) {
      alert('This PO has no remaining balance. Choose a PO with an open PO balance.');
      return;
    }

    // VAT optional: 18% of amount invoiced only when includeVat is checked
    const amountNum = parseFloat(newInvoiceForm.amountInvoiced.replace(/[^0-9.-]/g, '')) || 0;
    const vatAmount = includeVat ? amountNum * 0.18 : 0;
    const newInvoiceTotal = amountNum + vatAmount;
    const poOpenAmount = getPoOpenForInvoicing(poData);
    if (amountNum <= 0) {
      alert('Please enter a valid invoice amount greater than zero.');
      return;
    }
    if (newInvoiceTotal > poOpenAmount) {
      alert(
        `Invoice total cannot exceed the remaining PO balance (${formatCurrency(poOpenAmount)}).`
      );
      return;
    }

    const totalAmount = newInvoiceTotal;

    // Check for duplicate invoice ID (case-insensitive)
    try {
      const invoiceExists = await checkInvoiceExists(newInvoiceForm.invoiceNo.trim());
      if (invoiceExists) {
        alert(`Invoice with ID "${newInvoiceForm.invoiceNo}" already exists. Please use a different invoice number.`);
        setNewInvoiceForm(prev => ({ ...prev, invoiceNo: '' }));
        return;
      }
    } catch (error) {
      console.error('Error checking invoice existence:', error);
      alert('Error checking for duplicate invoice. Please try again.');
      return;
    }

    // Manual due date (stored as e.g. 23/Mar/2026)
    const dueDate = newInvoiceForm.dueDate;

    // Create new invoice object
    const newInvoiceData = {
      id: newInvoiceForm.invoiceNo,
      lot: poData.lot || '',
      subcategory: poData.subcategory || '',
      poId: newInvoiceForm.poNumber,
      prNo: (newInvoiceForm.prNo || '').trim(),
      vendor: poData.supplierName || '',
      description: poData.description || '',
      amount: `TZS ${amountNum.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      vat: `TZS ${vatAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      totalAmount: `TZS ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      status: 'Admin review',
      invoicedDate: newInvoiceForm.invoiceDate,
      dueDate: dueDate,
      paidDate: ''
    };

    try {
      setIsSavingInvoice(true);
      // Save to database
      const createdInvoice = await createInvoice(newInvoiceData);
      // Add to invoices list
      setInvoices(prev => [createdInvoice, ...prev]);
      
      // Update PO tracking table with new invoiced amount
      if (newInvoiceForm.poNumber) {
        try {
          await updatePOInvoicedAmount(newInvoiceForm.poNumber);
        } catch (poError) {
          console.error('Error updating PO:', poError);
          // Don't block the invoice creation if PO update fails
        }
      }
      
      // Reset form
      setNewInvoiceForm({
        invoiceNo: '',
        poNumber: '',
        prNo: '',
        invoiceDate: '',
        dueDate: '',
        amountInvoiced: ''
      });
      setIncludeVat(false);
      setPoData(null);
      setShowNewInvoiceModal(false);
      setInvoiceError(null);
      alert('Thank you, this invoice is successfully added');
    } catch (error) {
      console.error('Error creating invoice:', error);
      // Show specific error message if it's a duplicate
      if (error.message && error.message.includes('already exists')) {
        alert(error.message);
      } else {
        const detail = error?.message ? `\n\n${error.message}` : '';
        alert(`Failed to create invoice.${detail}`);
      }
    } finally {
      setIsSavingInvoice(false);
    }
  };

  // Handle opening new invoice modal
  const handleOpenNewInvoiceModal = () => {
    cancelEditInlinePrNo();
    setShowNewInvoiceModal(true);
    setNewInvoiceForm({
      invoiceNo: '',
      poNumber: '',
      prNo: '',
      invoiceDate: '',
      dueDate: '',
      amountInvoiced: ''
    });
    setIncludeVat(false);
    setPoData(null);
    setInvoiceError(null);
    setNewInvoiceLotFilter('');
  };

  // Handle closing new invoice modal
  const handleCloseNewInvoiceModal = () => {
    cancelEditInlinePrNo();
    cancelEditInlinePendingLevel();
    setShowNewInvoiceModal(false);
    setNewInvoiceForm({
      invoiceNo: '',
      poNumber: '',
      prNo: '',
      invoiceDate: '',
      dueDate: '',
      amountInvoiced: ''
    });
    setIncludeVat(false);
    setPoData(null);
    setInvoiceError(null);
    setNewInvoiceLotFilter('');
  };

  const cancelEditInlinePrNo = () => {
    setEditingPrRowKey('');
    setEditPrNoInlineValue('');
  };

  const cancelEditInlinePendingLevel = () => {
    setEditingPendingLevelRowKey('');
    setEditPendingLevelInlineValue('');
  };

  const startEditInlinePrNo = (invoice) => {
    if (isEditOpen) return;
    cancelEditInlinePendingLevel();
    const key = invoiceRowKey(invoice);
    if (!key) return;
    setEditingPrRowKey(key);
    setEditPrNoInlineValue(invoice.prNo || '');
  };

  const commitInlinePrNo = async (invoice) => {
    const rowKey = invoiceRowKey(invoice);
    if (!rowKey) {
      cancelEditInlinePrNo();
      return;
    }
    const existing = invoices.find((inv) => invoiceRowKey(inv) === rowKey);
    if (!existing) {
      cancelEditInlinePrNo();
      return;
    }
    const trimmed = editPrNoInlineValue.trim();
    if (trimmed === String(existing.prNo || '').trim()) {
      cancelEditInlinePrNo();
      return;
    }
    const documentId = existing.documentId;
    if (!documentId) {
      window.alert('Cannot save PR No: invoice record id is missing. Refresh the page and try again.');
      cancelEditInlinePrNo();
      return;
    }
    try {
      const updatedInvoice = await updateInvoice(documentId, { prNo: trimmed });
      setInvoices((prev) =>
        prev.map((inv) => (invoiceRowKey(inv) === rowKey ? updatedInvoice : inv))
      );
      cancelEditInlinePrNo();
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Failed to save PR No - Jazzcom.');
    }
  };

  const startEditInlinePendingLevel = (invoice) => {
    if (isEditOpen) return;
    if (!isPendingSignatureStatus(invoice.status)) return;
    const key = invoiceRowKey(invoice);
    if (!key) return;
    cancelEditInlinePrNo();
    setEditingPendingLevelRowKey(key);
    setEditPendingLevelInlineValue(invoice.pendingLevel || '');
  };

  const commitInlinePendingLevel = async (invoice) => {
    const rowKey = invoiceRowKey(invoice);
    if (!rowKey) {
      cancelEditInlinePendingLevel();
      return;
    }
    const existing = invoices.find((inv) => invoiceRowKey(inv) === rowKey);
    if (!existing) {
      cancelEditInlinePendingLevel();
      return;
    }
    if (!isPendingSignatureStatus(existing.status)) {
      cancelEditInlinePendingLevel();
      return;
    }
    const trimmed = editPendingLevelInlineValue.trim();
    if (trimmed === String(existing.pendingLevel || '').trim()) {
      cancelEditInlinePendingLevel();
      return;
    }
    const documentId = existing.documentId || existing.id;
    try {
      const updatedInvoice = await updateInvoice(documentId, { level: trimmed });
      setInvoices((prev) =>
        prev.map((inv) => (invoiceRowKey(inv) === rowKey ? updatedInvoice : inv))
      );
      cancelEditInlinePendingLevel();
    } catch (err) {
      console.error(err);
      window.alert(err?.message || 'Failed to save Pending level.');
    }
  };

  // Functions for editing invoices
  const handleEditClick = (invoice) => {
    cancelEditInlinePrNo();
    cancelEditInlinePendingLevel();
    setEditingInvoice(invoice);
    setEditStatus(invoice.status);
    setEditDueDate(invoice.dueDate || '');
    if (isInvoiceLogisticsPaidStatus(invoice.status)) {
      setEditPaidDate(invoice.paidDate || '');
    } else {
      setEditPaidDate('');
    }
    setIsEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (editingInvoice) {
      try {
        if (editDueDate && !parseInvoiceTrackingDate(editDueDate)) {
          alert('Please enter a valid due date (e.g. 23/Mar/2026).');
          return;
        }
        // Update in database
        const documentId = editingInvoice.documentId || editingInvoice.id;
        const finalPaidDate = isInvoiceLogisticsPaidStatus(editStatus) ? editPaidDate : '';
        const updatedInvoice = await updateInvoice(documentId, {
          status: editStatus,
          dueDate: editDueDate || '',
          paidDate: finalPaidDate
        });
        
        // Update in local state
        const updatedInvoices = invoices.map(inv => 
          (inv.documentId || inv.id) === documentId
            ? updatedInvoice
            : inv
        );
        setInvoices(updatedInvoices);
        
        // If invoice status is Paid, refresh PO tracking amounts
        if (editingInvoice.poId) {
          try {
            await updatePOInvoicedAmount(editingInvoice.poId);
          } catch (poError) {
            console.error('Error updating PO:', poError);
          }
        }
        
        setIsEditOpen(false);
        setEditingInvoice(null);
        setEditStatus('');
        setEditDueDate('');
        setEditPaidDate('');
        alert('Invoice updated successfully!');
      } catch (error) {
        console.error('Error updating invoice:', error);
        alert('Failed to update invoice. Please try again.');
      }
    }
  };

  // Helper function to update PO invoiced amount and balance
  const updatePOInvoicedAmount = async (poNumber) => {
    try {
      const allInvoices = await getAllInvoices();
      await syncPurchaseOrderInvoicing(poNumber, allInvoices);
    } catch (error) {
      console.error('Error updating PO invoiced amount:', error);
      throw error;
    }
  };

  const handleCancelEdit = () => {
    cancelEditInlinePrNo();
    cancelEditInlinePendingLevel();
    setIsEditOpen(false);
    setEditingInvoice(null);
    setEditStatus('');
    setEditDueDate('');
    setEditPaidDate('');
  };

  // Helper function to convert stored date → YYYY-MM-DD for date input
  const convertToDateInput = (dateString) => toHtmlDateInputValue(dateString);

  // Helper function to convert YYYY-MM-DD → 23/Mar/2026
  const convertToDateDisplay = (dateString) => fromHtmlDateInputValue(dateString);

  // Helper function to convert stored date → YYYY-MM-DD for date input
  const convertDateToInput = (dateString) => toHtmlDateInputValue(dateString);

  const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
  const CategoryIcon = selectedCategoryData?.icon || Package;

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setLotFilter('all');
    setStatusFilter('all');
    setCurrentPage(1);
  };

  // Export ALL invoices in invoice tracking (ignores current filters/category)
  const handleExportToExcel = async () => {
    try {
      const allInvoices = await getAllInvoices();

      if (!allInvoices || allInvoices.length === 0) {
        alert('No invoice data to export');
        return;
      }

      const toExcelDate = (dateString) => {
        const parsed = parseDate(dateString);
        return parsed || '';
      };

      const exportData = allInvoices.map((invoice) => ({
        'Invoice No': invoice.id || '',
        'PR No - Jazzcom': invoice.prNo || '',
        'LOT Name': invoice.lot || '',
        'Subcategory': invoice.subcategory || '',
        'PO Number': invoice.poId || '',
        'Vendor': invoice.vendor || '',
        'Description': invoice.description || '',
        'Invoiced Date': toExcelDate(invoice.invoicedDate),
        'Due Date': toExcelDate(invoice.dueDate),
        'Paid Date': toExcelDate(invoice.paidDate),
        'Amount': parseCurrency(invoice.amount),
        'VAT': parseCurrency(invoice.vat),
        'Total Amount': parseCurrency(invoice.totalAmount),
        'Status': invoice.status || '',
        'Pending level': invoice.pendingLevel || ''
      }));

      const ws = XLSX.utils.json_to_sheet(exportData, { cellDates: true });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Invoices');

      ws['!cols'] = [
        { wch: 20 }, // Invoice No
        { wch: 20 }, // PR No - Jazzcom
        { wch: 20 }, // LOT Name
        { wch: 25 }, // Subcategory
        { wch: 20 }, // PO Number
        { wch: 25 }, // Vendor
        { wch: 40 }, // Description
        { wch: 15 }, // Invoiced Date
        { wch: 15 }, // Due Date
        { wch: 15 }, // Paid Date
        { wch: 18 }, // Amount
        { wch: 15 }, // VAT
        { wch: 18 }, // Total Amount
        { wch: 12 }, // Status
        { wch: 14 }  // Pending level
      ];

      // Apply explicit Excel formatting for date and numeric columns
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let row = range.s.r + 1; row <= range.e.r; row += 1) {
        ['H', 'I', 'J'].forEach((col) => {
          const cellRef = `${col}${row + 1}`;
          if (ws[cellRef] && ws[cellRef].v) {
            ws[cellRef].z = 'dd/mm/yyyy';
          }
        });

        ['K', 'L', 'M'].forEach((col) => {
          const cellRef = `${col}${row + 1}`;
          if (ws[cellRef] && typeof ws[cellRef].v === 'number') {
            ws[cellRef].z = '#,##0.00';
          }
        });
      }

      const dateLabel = new Date().toISOString().split('T')[0];
      const fileName = `Invoice_Tracking_All_Invoices_${dateLabel}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error('Error exporting invoices to Excel:', err);
      alert(`Failed to export invoice data: ${err.message || 'Please try again.'}`);
    }
  };

  // Helper function to parse date from DD/Mon/YYYY or legacy DD/MM/YYYY
  const parseDate = (dateString) => parseInvoiceTrackingDate(dateString);

  // Filter invoices based on search query, status, and category
  const filteredInvoices = useMemo(() => invoices.filter(invoice => {
    // Apply category filter based on subcategory
    const invoiceCategory = getCategoryFromSubcategory(invoice.subcategory);
    if (selectedCategory !== invoiceCategory) {
      return false;
    }

    // Apply LOT filter (if invoice has lot property)
    if (lotFilter !== 'all' && invoice.lot && invoice.lot !== lotFilter) {
      return false;
    }

    // Apply status filter (value is lowercase key, e.g. "pending signature")
    if (statusFilter !== 'all') {
      if (invoiceStatusFilterKey(invoice.status) !== statusFilter) {
        return false;
      }
    }

    // Apply search query filter
    if (!searchQuery.trim()) {
      return true;
    }

    const query = searchQuery.toLowerCase().trim();
    
    // Extract numeric value from amount for searching
    const amountValue = invoice.amount.replace(/[^\d]/g, '');
    const totalAmountValue = invoice.totalAmount.replace(/[^\d]/g, '');
    
    // Search in multiple fields
    return (
      invoice.id.toLowerCase().includes(query) ||
      (invoice.lot && invoice.lot.toLowerCase().includes(query)) ||
      (invoice.subcategory && invoice.subcategory.toLowerCase().includes(query)) ||
      (invoice.poId && invoice.poId.toLowerCase().includes(query)) ||
      (invoice.prNo && invoice.prNo.toLowerCase().includes(query)) ||
      (invoice.vendor && invoice.vendor.toLowerCase().includes(query)) ||
      (invoice.description && invoice.description.toLowerCase().includes(query)) ||
      (invoice.status && invoice.status.toLowerCase().includes(query)) ||
      amountValue.includes(query) ||
      totalAmountValue.includes(query)
    );
  }), [invoices, selectedCategory, lotFilter, statusFilter, searchQuery]);

  const sortedFilteredInvoices = useMemo(() => {
    const list = [...filteredInvoices];
    list.sort((a, b) => {
      const byStatus = invoiceStatusSortRank(a.status) - invoiceStatusSortRank(b.status);
      if (byStatus !== 0) return byStatus;

      const dueA = parseInvoiceTrackingDate(a.dueDate);
      const dueB = parseInvoiceTrackingDate(b.dueDate);
      if (dueA && dueB) return dueA.getTime() - dueB.getTime();
      if (dueA && !dueB) return -1;
      if (!dueA && dueB) return 1;

      return String(a.id || '').localeCompare(String(b.id || ''), undefined, { numeric: true });
    });
    return list;
  }, [filteredInvoices]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, lotFilter, statusFilter, searchQuery]);

  const totalFiltered = sortedFilteredInvoices.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const indexOfFirstItem = totalFiltered > 0 ? (safePage - 1) * itemsPerPage + 1 : 0;
  const indexOfLastItem = Math.min(safePage * itemsPerPage, totalFiltered);
  const paginatedInvoices = useMemo(
    () => sortedFilteredInvoices.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage),
    [sortedFilteredInvoices, safePage, itemsPerPage]
  );

  const goToPage = (page) => {
    setCurrentPage(page);
    const tableWrapper = document.querySelector('.inv-table-wrapper');
    if (tableWrapper) {
      tableWrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const goToPreviousPage = () => {
    if (safePage > 1) goToPage(safePage - 1);
  };

  const goToNextPage = () => {
    if (safePage < totalPages) goToPage(safePage + 1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else if (safePage <= 3) {
      for (let i = 1; i <= 4; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    } else if (safePage >= totalPages - 2) {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      pageNumbers.push(1);
      pageNumbers.push('...');
      for (let i = safePage - 1; i <= safePage + 1; i++) pageNumbers.push(i);
      pageNumbers.push('...');
      pageNumbers.push(totalPages);
    }
    return pageNumbers;
  };

  // Calculate subtotals from filtered data (Total column = amount ex VAT, so same as amount)
  const calculateSubtotals = () => {
    const totals = filteredInvoices.reduce((acc, invoice) => {
      acc.amount += parseCurrency(invoice.amount);
      acc.vat += parseCurrency(invoice.vat);
      acc.totalAmount += parseCurrency(invoice.amount);
      return acc;
    }, {
      amount: 0,
      vat: 0,
      totalAmount: 0
    });
    return totals;
  };

  const subtotals = calculateSubtotals();

  // Helper function to check if due date has passed
  const isDueDatePassed = (dueDateString) => {
    if (!dueDateString) return false;
    const dueDate = parseDate(dueDateString);
    if (!dueDate) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    
    return dueDate < today;
  };

  return (
    <div className="inv-page">
      <div className="inv-scroll">
        <div className="inv-panel">
          <AppBar
            variant="inset"
            breadcrumbs={[
              { label: 'Platform', hideOnMobile: true },
              { label: 'Sagemcom', to: '/sagemcom' },
              { label: 'Logistics', to: '/sagemcom/logistics', hideOnMobile: true },
              { label: 'Invoice tracking' },
            ]}
          />

          {loading && invoices.length === 0 && !error ? (
            <InvPageSkeleton />
          ) : (
            <>
              <section className="inv-intro" aria-label="Invoice tracking overview">
                <div className="inv-intro-text">
                  <h1 className="inv-intro-title">Invoice tracking</h1>
                  <p className="inv-intro-subtitle">
                    Match invoices to purchase orders and track status, due dates, and payment
                  </p>
                </div>
                <div className="inv-intro-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="inv-soft-btn h-9 w-9"
                    onClick={handleExportToExcel}
                    disabled={invoices.length === 0}
                    title="Download Excel"
                    aria-label="Download Excel"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  {!isReadOnly ? (
                  <Button type="button" className="inv-create-btn" onClick={handleOpenNewInvoiceModal}>
                    <Plus className="h-4 w-4" />
                    New invoice
                  </Button>
                  ) : null}
                </div>
              </section>

              {error ? (
                <div className="inv-error" role="alert">
                  <span>{error}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="inv-soft-btn"
                    onClick={reloadInvoices}
                    disabled={loading}
                  >
                    {loading ? 'Retrying…' : 'Retry'}
                  </Button>
                </div>
              ) : null}

              <div className="inv-lot-tabs" role="tablist" aria-label="Filter by LOT">
                {PO_LOT_TAB_OPTIONS.filter(
                  (option) => !lockedLotFilter || option.value === lockedLotFilter
                ).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="tab"
                    aria-selected={lotFilter === option.value}
                    className={`inv-lot-tabs__btn${lotFilter === option.value ? ' is-active' : ''}`}
                    onClick={() => {
                      if (!lockedLotFilter) setLotFilter(option.value);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="inv-categories-grid">
                {categories.map((category) => {
                  const Icon = category.icon;
                  const isSelected = selectedCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      type="button"
                      className={`inv-category-card${isSelected ? ' is-selected' : ''}`}
                      onClick={() => setSelectedCategory(category.id)}
                      style={{ '--cat-accent': category.color }}
                      aria-pressed={isSelected}
                    >
                      <div className="inv-category-card__top">
                        <span className="inv-category-card__icon" aria-hidden>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="inv-category-card__label">{category.label}</span>
                        {isSelected ? (
                          <span className="inv-category-card__check" aria-hidden>
                            <Check className="h-3.5 w-3.5" />
                          </span>
                        ) : null}
                      </div>
                      <div className="inv-category-card__count">{category.invoiceCount} invoices</div>
                      <div className="inv-category-card__amount">{category.totalAmount}</div>
                      <div className="inv-category-card__meta">
                        <div>
                          <span>Invoiced</span>
                          <strong>{category.amountInvoiced}</strong>
                        </div>
                        <div>
                          <span>PO invoiced {category.poInvoicedPercent}</span>
                          <strong>{category.poInvoicedAmount}</strong>
                        </div>
                        <div>
                          <span>Pending {category.pendingPercent}</span>
                          <strong>{category.pendingAmount}</strong>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="inv-table-section__head">
                <div className="inv-table-section__title-row">
                  <CategoryIcon className="h-4 w-4" style={{ color: selectedCategoryData?.color }} aria-hidden />
                  <h2>
                    {selectedCategoryData?.label || 'Category'} details
                    <span className="inv-table-section__count">{filteredInvoices.length}</span>
                  </h2>
                </div>
              </div>

              <div className="inv-toolbar">
                <div className="inv-search">
                  <Search className="inv-search__icon h-4 w-4" aria-hidden />
                  <Input
                    className="inv-search__input"
                    placeholder="Search invoice, PO, supplier, description…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label="Search invoices"
                  />
                </div>
                <div className="inv-toolbar__spacer" aria-hidden="true" />
                <div className="inv-filters">
                  <select
                    className="inv-filter-select"
                    value={lotFilter}
                    onChange={(e) => setLotFilter(e.target.value)}
                    aria-label="LOT filter"
                  >
                    <option value="all">All LOTs</option>
                    <option value="LOT 8 (Morogoro)">LOT 8 (Morogoro)</option>
                    <option value="LOT 10 (Manyara)">LOT 10 (Manyara)</option>
                  </select>
                  <select
                    className="inv-filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    aria-label="Status filter"
                  >
                    <option value="all">All Status</option>
                    {allInvoiceStatusesForUi.map((label) => (
                      <option key={label} value={invoiceStatusFilterKey(label)}>
                        {label}
                      </option>
                    ))}
                  </select>
                  {(searchQuery || lotFilter !== 'all' || statusFilter !== 'all') ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="inv-soft-btn"
                      onClick={clearAllFilters}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                      Clear
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="inv-table-section">
                <div className="inv-table-shell">
                  <div className="inv-table-wrapper">
                    <table className="inv-table">
                      {filteredInvoices.length > 0 ? (
                        <thead className="subtotal-header">
                          <tr className="subtotal-row">
                            <th colSpan={7}>
                              Subtotal ({filteredInvoices.length}{' '}
                              {filteredInvoices.length === 1 ? 'item' : 'items'})
                            </th>
                            <th>{formatCurrency(subtotals.amount)}</th>
                            <th>{formatCurrency(subtotals.vat)}</th>
                            <th>{formatCurrency(subtotals.totalAmount)}</th>
                            <th colSpan={6} />
                          </tr>
                        </thead>
                      ) : null}
                      <thead>
                        <tr>
                          <th className="inv-col-pr">PR No</th>
                          <th className="inv-col-lot">LOT</th>
                          <th className="inv-col-invoice">Invoice No</th>
                          <th className="inv-col-subcategory">Subcategory</th>
                          <th className="inv-col-po">PO Number</th>
                          <th className="inv-col-supplier">Supplier</th>
                          <th className="inv-col-description">Description</th>
                          <th className="inv-col-amount">Amount</th>
                          <th className="inv-col-amount">VAT</th>
                          <th className="inv-col-amount">Total</th>
                          <th className="inv-col-status">Status</th>
                          <th className="inv-col-pending-level">Pending level</th>
                          <th className="inv-col-date">Invoiced</th>
                          <th className="inv-col-date">Due</th>
                          <th className="inv-col-date">Paid</th>
                          <th className="inv-col-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          Array.from({ length: 6 }, (_, r) => (
                            <tr key={`skel-${r}`} className="inv-table-skel-row">
                              {Array.from({ length: 16 }, (_, c) => (
                                <td key={c}>
                                  <Skeleton className="h-4 w-[85%]" />
                                </td>
                              ))}
                            </tr>
                          ))
                        ) : paginatedInvoices.length === 0 ? (
                          <tr>
                            <td colSpan={16} className="inv-table-empty">
                              No invoices match this category and filters.
                            </td>
                          </tr>
                        ) : (
                          <>
                            {paginatedInvoices.map((invoice) => {
                const rowKey = invoiceRowKey(invoice);
                const isEditingPr = editingPrRowKey === rowKey;
                const isEditingPendingLevel = editingPendingLevelRowKey === rowKey;
                const canEditPendingLevel = isPendingSignatureStatus(invoice.status);
                const rowInlineBusy = isEditingPr || isEditingPendingLevel;
                const amountWarning = invoiceAmountWarnings.get(rowKey);
                const amountCellClass = amountWarning
                  ? 'invoice-col-amount amount-exceeds-po'
                  : 'invoice-col-amount';
                return (
                <tr key={rowKey}>
                  <td className="inv-pr-cell invoice-col-pr">
                    {isEditingPr ? (
                      <input
                        type="text"
                        className="inv-pr-input"
                        value={editPrNoInlineValue}
                        onChange={(e) => setEditPrNoInlineValue(e.target.value)}
                        onBlur={() => commitInlinePrNo(invoice)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelEditInlinePrNo();
                          }
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.currentTarget.blur();
                          }
                        }}
                        autoComplete="off"
                        autoFocus
                        maxLength={64}
                        disabled={isEditOpen}
                        aria-label="PR No - Jazzcom"
                      />
                    ) : (
                      <button
                        type="button"
                        className="inv-pr-hit"
                        onClick={() => startEditInlinePrNo(invoice)}
                        disabled={isEditOpen || rowInlineBusy}
                        title={isEditOpen || rowInlineBusy ? undefined : 'Click to edit PR No - Jazzcom'}
                      >
                        {invoice.prNo ? invoice.prNo : '—'}
                      </button>
                    )}
                  </td>
                  <td className="invoice-col-lot" title={invoice.lot ? String(invoice.lot).trim() : undefined}>
                    {formatLotNameTableCell(invoice.lot)}
                  </td>
                  <td className="invoice-id invoice-col-invoice inv-col-invoice">{invoice.id}</td>
                  <td className="invoice-col-subcategory">
                    <span className="inv-badge" style={{ backgroundColor: `${selectedCategoryData?.color}15`, color: selectedCategoryData?.color }}>
                      {invoice.subcategory}
                    </span>
                  </td>
                  <td className="invoice-col-po">
                    <span className="inv-badge inv-badge--po" style={{ backgroundColor: `${selectedCategoryData?.color}15`, color: selectedCategoryData?.color }}>
                      {invoice.poId}
                    </span>
                  </td>
                  <td className="invoice-col-supplier" title={invoice.vendor ? String(invoice.vendor).trim() : undefined}>
                    {formatSupplierNameTableCell(invoice.vendor)}
                  </td>
                  <td
                    className="invoice-col-description"
                    title={invoice.description ? String(invoice.description).trim() : undefined}
                  >
                    {truncateDescriptionWords(invoice.description)}
                  </td>
                  <td
                    className={amountCellClass}
                    title={amountWarning || undefined}
                  >
                    {invoice.amount}
                  </td>
                  <td className="invoice-col-amount">{invoice.vat}</td>
                  <td
                    className={amountWarning ? 'invoice-col-amount total-amount amount-exceeds-po' : 'invoice-col-amount total-amount'}
                    title={amountWarning || undefined}
                  >
                    {invoice.amount}
                  </td>
                  <td className="invoice-col-status">
                    <span className={`inv-status inv-status--${invoiceStatusBadgeClass(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="inv-pending-level-cell invoice-col-pending-level">
                    {isEditingPendingLevel ? (
                      <input
                        type="text"
                        className="inv-pr-input inv-pending-level-input"
                        value={editPendingLevelInlineValue}
                        onChange={(e) => setEditPendingLevelInlineValue(e.target.value)}
                        onBlur={() => commitInlinePendingLevel(invoice)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelEditInlinePendingLevel();
                          }
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.currentTarget.blur();
                          }
                        }}
                        autoComplete="off"
                        autoFocus
                        maxLength={64}
                        disabled={isEditOpen}
                        aria-label="Pending level"
                      />
                    ) : canEditPendingLevel ? (
                      <button
                        type="button"
                        className="inv-pr-hit inv-pending-level-hit"
                        onClick={() => startEditInlinePendingLevel(invoice)}
                        disabled={isEditOpen || rowInlineBusy}
                        title={
                          isEditOpen || rowInlineBusy
                            ? undefined
                            : 'Click to edit Pending level (Pending Signature only)'
                        }
                      >
                        {invoice.pendingLevel ? invoice.pendingLevel : '—'}
                      </button>
                    ) : (
                      <span
                        className="inv-pending-level-readonly"
                        title={
                          invoice.pendingLevel
                            ? invoice.pendingLevel
                            : 'Editable only when status is Pending Signature'
                        }
                      >
                        {invoice.pendingLevel ? invoice.pendingLevel : '—'}
                      </span>
                    )}
                  </td>
                  <td className="invoice-col-date">{displayInvoiceTrackingDate(invoice.invoicedDate)}</td>
                  <td
                    className="invoice-col-date"
                    style={{
                      color: isInvoiceLogisticsPaidStatus(invoice.status) ? '#22c55e' : (isDueDatePassed(invoice.dueDate) ? '#ef4444' : undefined),
                      fontWeight: (isInvoiceLogisticsPaidStatus(invoice.status) || isDueDatePassed(invoice.dueDate)) ? '600' : '400'
                    }}
                  >
                    {displayInvoiceTrackingDate(invoice.dueDate)}
                  </td>
                  <td
                    className="invoice-col-date"
                    style={{
                      color: (isInvoiceLogisticsPaidStatus(invoice.status) && invoice.paidDate) ? '#22c55e' : undefined,
                      fontWeight: (isInvoiceLogisticsPaidStatus(invoice.status) && invoice.paidDate) ? '600' : '400'
                    }}
                  >
                    {isInvoiceLogisticsPaidStatus(invoice.status)
                      ? (displayInvoiceTrackingDate(invoice.paidDate) || '-')
                      : ''}
                  </td>
                  <td className="invoice-col-actions">
                    <button
                      type="button"
                      onClick={() => handleEditClick(invoice)}
                      className="inv-edit-btn"
                      title="Edit Status, Due Date & Paid Date"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
                );
                            })}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {totalFiltered > 0 ? (
                  <div className="inv-pagination">
                    <div className="inv-pagination__info">
                      Showing {indexOfFirstItem}–{indexOfLastItem} of {totalFiltered}
                    </div>
                    <div className="inv-pagination__controls">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="inv-soft-btn h-8 w-8"
                        onClick={goToPreviousPage}
                        disabled={safePage === 1}
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="inv-pagination__numbers">
                        {getPageNumbers().map((pageNum, index) =>
                          pageNum === '...' ? (
                            <span key={`ellipsis-${index}`} className="inv-pagination__ellipsis">
                              …
                            </span>
                          ) : (
                            <button
                              key={pageNum}
                              type="button"
                              className={`inv-pagination__num${safePage === pageNum ? ' is-active' : ''}`}
                              onClick={() => goToPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          )
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="inv-soft-btn h-8 w-8"
                        onClick={goToNextPage}
                        disabled={safePage === totalPages}
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      {isEditOpen && (
        <div className="inv-modal-overlay" onClick={handleCancelEdit}>
          <div className="inv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="inv-modal__header">
              <h3>Edit Invoice Status & Dates</h3>
              <button type="button" className="inv-soft-btn inv-modal__close" onClick={handleCancelEdit} aria-label="Close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="inv-modal__body">
              {editingInvoice && (
                <>
                  <div className="inv-field">
                    <label>Invoice No</label>
                    <input type="text" value={editingInvoice.id} disabled className="inv-input" />
                  </div>
                  <div className="inv-field">
                    <label>Status *</label>
                    <select 
                      value={editStatus} 
                      onChange={(e) => {
                        const newStatus = e.target.value;
                        setEditStatus(newStatus);
                        if (!isInvoiceLogisticsPaidStatus(newStatus)) {
                          setEditPaidDate('');
                        }
                      }}
                      className="inv-select"
                    >
                      {allInvoiceStatusesForUi.map((label) => (
                        <option key={label} value={label}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="inv-field">
                    <label>Due Date</label>
                    <input
                      type="date"
                      value={editDueDate ? toHtmlDateInputValue(editDueDate) : ''}
                      onChange={(e) => {
                        setEditDueDate(e.target.value ? fromHtmlDateInputValue(e.target.value) : '');
                      }}
                      className="inv-input"
                    />
                    <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                      Format: DD/Mon/YYYY (e.g. 23/Mar/2026)
                      {editDueDate ? ` — saved as ${editDueDate}` : ''}
                    </small>
                  </div>
                  {isInvoiceLogisticsPaidStatus(editStatus) && (
                    <div className="inv-field">
                      <label>Paid Date</label>
                      <input 
                        type="date" 
                        value={editPaidDate ? convertToDateInput(editPaidDate) : ''}
                        onChange={(e) => {
                          const date = e.target.value;
                          setEditPaidDate(date ? convertToDateDisplay(date) : '');
                        }}
                        className="inv-input"
                      />
                      <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                        Format: DD/Mon/YYYY (e.g. 23/Mar/2026)
                        {editPaidDate ? ` — saved as ${editPaidDate}` : ''}
                      </small>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="inv-modal__actions">
              <button className="inv-soft-btn" onClick={handleCancelEdit}>Cancel</button>
              <button className="inv-save-btn" onClick={handleSaveEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* New Invoice Modal */}
      {showNewInvoiceModal && (
        <div className="inv-modal-overlay" onClick={handleCloseNewInvoiceModal}>
          <div className="inv-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="inv-modal__header">
              <h3>New Invoice</h3>
              <button className="inv-soft-btn inv-modal__close" onClick={handleCloseNewInvoiceModal}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={handleNewInvoiceSubmit}>
              <div className="inv-modal__body">
                <div className="inv-field">
                  <label>Invoice No *</label>
                  <input
                    type="text"
                    value={newInvoiceForm.invoiceNo}
                    onChange={(e) => setNewInvoiceForm(prev => ({ ...prev, invoiceNo: e.target.value }))}
                    className="inv-input"
                    placeholder="e.g., INV-2025-001"
                    required
                  />
                </div>

                {(() => {
                  /** Unique LOTs from the loaded POs, alpha-sorted. */
                  const lotOptions = Array.from(
                    new Set(
                      availablePOs
                        .map((po) => String(po.lot || '').trim())
                        .filter((lot) => lot !== '')
                    )
                  ).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

                  /** POs limited to the chosen LOT (or all if no filter). */
                  const filteredPosForDialog = newInvoiceLotFilter
                    ? availablePOs.filter(
                        (po) => String(po.lot || '').trim() === newInvoiceLotFilter
                      )
                    : availablePOs;

                  return (
                    <>
                      <div className="inv-field">
                        <label>
                          Filter by LOT
                          {lotOptions.length > 0 ? (
                            <small style={{ marginLeft: '0.4rem', color: '#64748b', fontWeight: 500 }}>
                              (optional — narrows the PO list below)
                            </small>
                          ) : null}
                        </label>
                        <div style={{ position: 'relative' }}>
                          <select
                            value={newInvoiceLotFilter}
                            onChange={(e) => {
                              const nextLot = e.target.value;
                              setNewInvoiceLotFilter(nextLot);
                              // Clear the PO field if the previously-selected PO is not in the new LOT.
                              if (nextLot && newInvoiceForm.poNumber) {
                                const stillMatches = availablePOs.some(
                                  (po) =>
                                    po.poNumber === newInvoiceForm.poNumber &&
                                    String(po.lot || '').trim() === nextLot
                                );
                                if (!stillMatches) {
                                  handlePONumberChange('');
                                }
                              }
                            }}
                            className="inv-select"
                            disabled={loadingPOs || lotOptions.length === 0}
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              fontSize: '0.9rem',
                              color: '#1a202c',
                              background: loadingPOs ? '#f7fafc' : 'white',
                              cursor: loadingPOs || lotOptions.length === 0 ? 'not-allowed' : 'pointer',
                              appearance: 'none'
                            }}
                          >
                            <option value="">
                              All LOTs ({availablePOs.length} PO{availablePOs.length === 1 ? '' : 's'} with balance)
                            </option>
                            {lotOptions.map((lot) => {
                              const count = availablePOs.filter(
                                (po) => String(po.lot || '').trim() === lot
                              ).length;
                              return (
                                <option key={lot} value={lot}>
                                  {lot} ({count} PO{count === 1 ? '' : 's'})
                                </option>
                              );
                            })}
                          </select>
                          <ChevronDown className="inv-select-chevron"
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#718096',
                              pointerEvents: 'none'
                            }}
                          />
                        </div>
                      </div>

                      <div className="inv-field">
                        <label>
                          PO Number *
                          {filteredPosForDialog.length > 0 ? (
                            <small style={{ marginLeft: '0.4rem', color: '#64748b', fontWeight: 500 }}>
                              ({filteredPosForDialog.length} PO{filteredPosForDialog.length === 1 ? '' : 's'}
                              {newInvoiceLotFilter ? ` in ${newInvoiceLotFilter}` : ''} with open balance — type to search)
                            </small>
                          ) : !loadingPOs ? (
                            <small style={{ marginLeft: '0.4rem', color: '#64748b', fontWeight: 500 }}>
                              (no POs with remaining balance
                              {newInvoiceLotFilter ? ` in ${newInvoiceLotFilter}` : ''})
                            </small>
                          ) : null}
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="text"
                            list="invoice-po-options"
                            value={newInvoiceForm.poNumber}
                            onChange={(e) => handlePONumberChange(e.target.value.trim())}
                            placeholder={
                              loadingPOs
                                ? 'Loading POs…'
                                : newInvoiceLotFilter
                                  ? `Type PO number or supplier in ${newInvoiceLotFilter}`
                                  : 'Type PO number or supplier (e.g. 551001167 or Honle)'
                            }
                            className="inv-input"
                            required
                            disabled={loadingPOs}
                            autoComplete="off"
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              fontSize: '0.9rem',
                              color: '#1a202c',
                              background: loadingPOs ? '#f7fafc' : 'white',
                              cursor: loadingPOs ? 'not-allowed' : 'text'
                            }}
                          />
                          <datalist id="invoice-po-options">
                            {filteredPosForDialog
                              .slice()
                              .sort((a, b) =>
                                String(a.poNumber || '').localeCompare(
                                  String(b.poNumber || ''),
                                  undefined,
                                  { numeric: true }
                                )
                              )
                              .map((po) => (
                                <option
                                  key={po.id}
                                  value={po.poNumber}
                                  label={`${po.supplierName || 'Unknown'} — ${po.lot || 'No LOT'}`}
                                />
                              ))}
                          </datalist>
                        </div>
                  {loadingPOs && (
                    <small style={{ color: '#3b82f6', marginTop: '0.25rem', display: 'block' }}>
                      Loading PO list...
                    </small>
                  )}
                  {loadingPO && (
                    <small style={{ color: '#3b82f6', marginTop: '0.25rem', display: 'block' }}>
                      Loading PO data...
                    </small>
                  )}
                  {invoiceError && (
                    <small style={{ color: '#ef4444', marginTop: '0.25rem', display: 'block' }}>
                      {invoiceError}
                    </small>
                  )}
                  {poData && !loadingPO && (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      padding: '0.75rem', 
                      background: '#f0fdf4', 
                      border: '1px solid #86efac', 
                      borderRadius: '8px',
                      fontSize: '0.875rem'
                    }}>
                      <div style={{ fontWeight: '600', color: '#15803d', marginBottom: '0.5rem' }}>PO Details:</div>
                      <div style={{ color: '#166534' }}>
                        <div><strong>LOT:</strong> {poData.lot || '-'}</div>
                        <div><strong>Subcategory:</strong> {poData.subcategory || '-'}</div>
                        <div><strong>Supplier:</strong> {poData.supplierName || '-'}</div>
                        <div><strong>Description:</strong> {poData.description || '-'}</div>
                        <div><strong>PO Total (incl. VAT):</strong> {poData.totalAmount || '-'}</div>
                        <div><strong>Amount invoiced:</strong> {poData.invoicedAmount || '-'}</div>
                        <div><strong>PO balance:</strong> {poData.poBalance || '-'}</div>
                      </div>
                    </div>
                  )}
                      </div>
                    </>
                  );
                })()}

                <div className="inv-field">
                  <label>PR No - Jazzcom</label>
                  <input
                    type="text"
                    value={newInvoiceForm.prNo}
                    onChange={(e) => setNewInvoiceForm(prev => ({ ...prev, prNo: e.target.value }))}
                    className="inv-input"
                    placeholder="Optional"
                    maxLength={64}
                  />
                </div>

                <div className="inv-field">
                  <label>Invoice Date *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="date"
                      value={newInvoiceForm.invoiceDate ? convertDateToInput(newInvoiceForm.invoiceDate) : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value;
                        setNewInvoiceForm((prev) => ({
                          ...prev,
                          invoiceDate: dateValue ? fromHtmlDateInputValue(dateValue) : ''
                        }));
                      }}
                      className="inv-input"
                      required
                      style={{ 
                        width: '100%',
                        padding: '0.75rem',
                        paddingRight: '2.5rem',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        color: '#1a202c'
                      }}
                    />
                    <FileText className="inv-date-icon" aria-hidden 
                      style={{ 
                        position: 'absolute', 
                        right: '0.75rem', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        color: '#718096',
                        pointerEvents: 'none'
                      }} 
                    />
                  </div>
                  <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                    Format: DD/Mon/YYYY (e.g. 23/Mar/2026)
                    {newInvoiceForm.invoiceDate ? ` — saved as ${newInvoiceForm.invoiceDate}` : ''}
                  </small>
                </div>

                <div className="inv-field">
                  <label>Due Date *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="date"
                      value={newInvoiceForm.dueDate ? toHtmlDateInputValue(newInvoiceForm.dueDate) : ''}
                      onChange={(e) => {
                        const dateValue = e.target.value;
                        setNewInvoiceForm((prev) => ({
                          ...prev,
                          dueDate: dateValue ? fromHtmlDateInputValue(dateValue) : ''
                        }));
                      }}
                      className="inv-input"
                      required
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        paddingRight: '2.5rem',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        color: '#1a202c'
                      }}
                    />
                    <FileText className="inv-date-icon" aria-hidden
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#718096',
                        pointerEvents: 'none'
                      }}
                    />
                  </div>
                  <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                    Format: DD/Mon/YYYY (e.g. 23/Mar/2026)
                    {newInvoiceForm.dueDate ? ` — saved as ${newInvoiceForm.dueDate}` : ''}
                  </small>
                </div>

                <div className="inv-field">
                  <label>Amount Invoiced *</label>
                  <input
                    type="text"
                    value={newInvoiceForm.amountInvoiced}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setNewInvoiceForm(prev => ({ ...prev, amountInvoiced: value }));
                    }}
                    className="inv-input"
                    placeholder="e.g., 50000000"
                    required
                  />
                  <small style={{ color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                    Enter amount in TZS (numbers only)
                    {poData && (
                      <> — max {formatCurrency(getPoOpenForInvoicing(poData))}</>
                    )}
                  </small>
                  {poData && newInvoiceForm.amountInvoiced && (() => {
                    const entered = parseFloat(newInvoiceForm.amountInvoiced.replace(/[^0-9.-]/g, '')) || 0;
                    const enteredTotal = entered + (includeVat ? entered * 0.18 : 0);
                    const open = getPoOpenForInvoicing(poData);
                    if (enteredTotal > open) {
                      return (
                        <small style={{ color: '#ef4444', marginTop: '0.25rem', display: 'block' }}>
                          Invoice total cannot exceed the remaining PO balance ({formatCurrency(open)}).
                        </small>
                      );
                    }
                    return null;
                  })()}
                  <label className="include-vat-checkbox" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', fontSize: '0.875rem', fontWeight: 500, color: '#374151', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={includeVat}
                      onChange={(e) => setIncludeVat(e.target.checked)}
                      style={{ width: '1rem', height: '1rem', accentColor: '#2563eb', cursor: 'pointer' }}
                    />
                    <span>Include VAT (18%)</span>
                  </label>
                  {newInvoiceForm.amountInvoiced && (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      padding: '0.5rem', 
                      background: '#f8fafc', 
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      color: '#475569'
                    }}>
                      <div>Amount: TZS {parseFloat(newInvoiceForm.amountInvoiced || 0).toLocaleString('en-US')}</div>
                      <div>VAT (18%): TZS {(includeVat ? parseFloat(newInvoiceForm.amountInvoiced || 0) * 0.18 : 0).toLocaleString('en-US')}</div>
                      <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>
                        Total: TZS {(parseFloat(newInvoiceForm.amountInvoiced || 0) + (includeVat ? parseFloat(newInvoiceForm.amountInvoiced || 0) * 0.18 : 0)).toLocaleString('en-US')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="inv-modal__actions">
                <button type="button" className="inv-soft-btn" onClick={handleCloseNewInvoiceModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="inv-save-btn"
                  disabled={
                    isSavingInvoice ||
                    !poData ||
                    loadingPO ||
                    (() => {
                      const entered = parseFloat(newInvoiceForm.amountInvoiced.replace(/[^0-9.-]/g, '')) || 0;
                      const enteredTotal = entered + (includeVat ? entered * 0.18 : 0);
                      return entered <= 0 || enteredTotal > getPoOpenForInvoicing(poData);
                    })()
                  }
                >
                  {isSavingInvoice ? 'Saving…' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvoiceTracking;
